import { AddRegular, DeleteRegular, EditRegular } from "@fluentui/react-icons";
import {
  adminDeleteSurveyTemplate,
  adminGetSurveyTemplates,
} from "apps/project-iq/api";
import { IDiagnosticTemplate } from "apps/project-iq/models/IDiagnosticTemplate";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { AdminListPage } from "components/adminList/AdminListPage";
import {
  IAdminListAction,
  IAdminRowAction,
} from "components/adminList/AdminListPage.types";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { useAdminListPagination } from "hooks/useAdminListPagination";
import { useAdminListSearch } from "hooks/useAdminListSearch";
import { useAdminListSort } from "hooks/useAdminListSort";
import { ITableColumn } from "models/ITableColumn";
import { useEffect, useMemo, useState } from "react";
import { useManageSurveyTemplatesStyles } from "./ManageSurveyTemplates.styles";
import { ISurveyTemplateDialogState } from "./ManageSurveyTemplates.types";
import { EditSurveyTemplateDialog } from "./EditSurveyTemplateDialog";

export const ManageSurveyTemplates = (): JSX.Element => {
  const colStyles = useAdminColumnStyles();
  const styles = useManageSurveyTemplatesStyles();

  const [templates, setTemplates] = useState<IDiagnosticTemplate[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogState, setDialogState] = useState<ISurveyTemplateDialogState>({
    open: false,
    template: null,
  });
  const [deleteTarget, setDeleteTarget] = useState<IDiagnosticTemplate | null>(
    null,
  );

  const loadTemplates = async (silent = false): Promise<void> => {
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      const data = await adminGetSurveyTemplates();
      setTemplates(data as IDiagnosticTemplate[]);
    } catch {
      setError("Failed to load survey templates. Please try again.");
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadTemplates();
  }, []);

  const handleConfirmDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    try {
      await adminDeleteSurveyTemplate(deleteTarget.id);
      setDeleteTarget(null);
      void loadTemplates();
    } catch {
      setError("Failed to delete survey template. Please try again.");
      setDeleteTarget(null);
    }
  };

  const handleSave = (): void => {
    setDialogState({ open: false, template: null });
    void loadTemplates();
  };

  const handleDismiss = (): void => {
    setDialogState({ open: false, template: null });
  };

  const searchFields = useMemo(
    () => ["name", "category"] as (keyof IDiagnosticTemplate)[],
    [],
  );

  const { searchQuery, handleSearchChange, filteredItems } =
    useAdminListSearch<IDiagnosticTemplate>({
      items: templates,
      searchFields,
    });

  const {
    sortColumn,
    isSortDescending,
    sortItems,
    onSortColumnChanged,
    onClearColumnChanged,
  } = useAdminListSort<IDiagnosticTemplate>({
    cacheKey: "IqSurveyTemplates",
    defaultSortColumn: "name",
    defaultDescending: false,
  });

  const columns: ITableColumn<IDiagnosticTemplate>[] = useMemo(
    () => [
      {
        columnId: "name",
        displayValue: "Name",
        className: colStyles.wide,
        renderHeaderCell: () => "Name",
        compare: (a, b) => a.name.localeCompare(b.name),
        renderCell: (item) => <span>{item.name}</span>,
      },
      {
        columnId: "category",
        displayValue: "Category",
        className: colStyles.normal,
        renderHeaderCell: () => "Category",
        compare: (a, b) => a.category.localeCompare(b.category),
        renderCell: (item) => (
          <span className={styles.categoryPill}>{item.category}</span>
        ),
      },
      {
        columnId: "isActive",
        displayValue: "Status",
        className: colStyles.normal,
        renderHeaderCell: () => "Status",
        compare: (a, b) =>
          Number(a.isActive ?? true) - Number(b.isActive ?? true),
        renderCell: (item) => {
          const isActive = item.isActive ?? true;
          return (
            <span
              className={isActive ? styles.activeCell : styles.inactiveCell}
            >
              {isActive ? "Active" : "Inactive"}
            </span>
          );
        },
      },
    ],
    [colStyles, styles],
  );

  const sortedItems = useMemo(
    () => sortItems(filteredItems, columns),
    [filteredItems, sortItems, columns],
  );

  const {
    paginatedItems,
    selectedPageIndex,
    pageCount,
    handlePageChange,
    totalItemsCount,
    itemsPerPage,
  } = useAdminListPagination<IDiagnosticTemplate>({
    items: sortedItems,
    itemsPerPage: 15,
  });

  const rowActions: IAdminRowAction<IDiagnosticTemplate>[] = useMemo(
    () => [
      {
        icon: <EditRegular />,
        tooltip: "Edit",
        onClick: (item) => setDialogState({ open: true, template: item }),
        disabled: () => false,
      },
      {
        icon: <DeleteRegular />,
        tooltip: "Delete",
        onClick: (item) => setDeleteTarget(item),
        disabled: () => false,
      },
    ],
    [],
  );

  const headerActions: IAdminListAction[] = useMemo(
    () => [
      {
        label: "Add New",
        icon: <AddRegular />,
        appearance: "primary",
        onClick: () => setDialogState({ open: true, template: null }),
      },
    ],
    [],
  );

  return (
    <>
      <AdminListPage<IDiagnosticTemplate>
        title="Survey Templates"
        items={templates}
        columns={columns}
        isLoading={isLoading}
        headerActions={headerActions}
        searchPlaceholder="Search templates..."
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        rowActions={rowActions}
        getRowId={(item) => item.id}
        sortColumn={sortColumn}
        isSortDescending={isSortDescending}
        onSortChange={onSortColumnChanged}
        onSortClear={onClearColumnChanged}
        paginatedItems={paginatedItems}
        selectedPageIndex={selectedPageIndex}
        pageCount={pageCount}
        totalItemsCount={totalItemsCount}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        emptyStateMessage={
          error ??
          (searchQuery
            ? "No templates match the search."
            : "No survey templates found.")
        }
        actionsWidth="100px"
      />
      <EditSurveyTemplateDialog
        open={dialogState.open}
        template={dialogState.template}
        onSave={handleSave}
        onDismiss={handleDismiss}
      />
      <ActionDialog
        isVisible={deleteTarget !== null}
        title="Delete Survey Template"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? This cannot be undone.`}
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        onConfirmation={handleConfirmDelete}
        onDismiss={() => setDeleteTarget(null)}
      />
    </>
  );
};
